"use client";

import delay from "delay";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";

import Preloader from "@/app/components/preloader";
import useCheckout from "@/app/hooks/useCheckout";

import style from "./preparingCheckout.module.scss";

export default function PreparingCheckout({ performGoToNextStepOnLoad = true }: PreparingCheckoutProps) {
  const router = useRouter();
  const { account, checkoutCart, checkoutIsLoading, setupComplete, goToNextStep } = useCheckout({
    performInitialSetup: true,
  });
  const [showPreloader, setShowPreloader] = useState(false);
  const hasNavigatedRef = useRef(false);

  const displayPlaceholderTimeout = useCallback(async () => {
    await delay(500);
    setShowPreloader(true);
  }, []);
  useEffect(() => {
    displayPlaceholderTimeout();
  }, [displayPlaceholderTimeout]);

  // Replace instead of push to prevent the back button from getting stuck on the preparation step.
  // Navigates once, after setup has picked the saved address and card. Setup needs an account and
  // a cart with products, so a signed-out visitor goes on to login and an empty cart goes back to the cart.
  useEffect(() => {
    if (!performGoToNextStepOnLoad || checkoutIsLoading === true || hasNavigatedRef.current) return;
    const cartIsEmpty = !checkoutCart?.vendorOrders.some((vendorOrder) => vendorOrder.products.length > 0);
    if (account && cartIsEmpty) {
      hasNavigatedRef.current = true;
      router.replace("/shopping-cart");
    } else if (setupComplete || !account) {
      hasNavigatedRef.current = true;
      goToNextStep({ replace: true });
    }
  }, [performGoToNextStepOnLoad, checkoutIsLoading, checkoutCart, setupComplete, account, goToNextStep, router]);

  return (
    <div className="d-flex justify-content-center align-items-center flex-column">
      {showPreloader && <Preloader className={style.preloader} textUnderAnimation="Preparing checkout..." />}
    </div>
  );
}

export type PreparingCheckoutProps = {
  performGoToNextStepOnLoad?: boolean;
};
